import { fetchCompanyDirectory } from './companyDirectory'
import { supabase } from './supabaseClient'
import { countSuccessfulCollaborationsForCompany } from './needCompletions'

export const HOME_SECTION_TIMEOUT_MS = 8000
const HOME_COMPANY_TIMEOUT_MS = 12000
// Share only live work for the same account, never retain a data cache.
const pending = new Map()
const scopedPending = new WeakMap()

const pendingFor = scope => {
  if (!scope || typeof scope !== 'object') return pending
  let jobs = scopedPending.get(scope)
  if (!jobs) { jobs = new Map(); scopedPending.set(scope, jobs) }
  return jobs
}

const abortError = () => Object.assign(new Error('Home request aborted'), { name: 'AbortError' })
const queryWithSignal = (query, signal) => signal ? query.abortSignal(signal) : query
const withinDeadline = (work, milliseconds = HOME_SECTION_TIMEOUT_MS, parentSignal) => new Promise((resolve, reject) => {
  const controller = new AbortController()
  let settled = false
  const finish = (complete, value) => {
    if (settled) return
    settled = true
    clearTimeout(timer)
    parentSignal?.removeEventListener('abort', abort)
    complete(value)
  }
  const abort = () => { finish(reject, abortError()); controller.abort() }
  const timer = setTimeout(() => {
    finish(reject, Object.assign(new Error('Home request timed out'), { code: 'HOME_TIMEOUT' }))
    controller.abort()
  }, milliseconds)
  parentSignal?.addEventListener('abort', abort, { once: true })
  if (parentSignal?.aborted) abort()
  else Promise.resolve().then(() => {
    if (controller.signal.aborted) throw abortError()
    return work(controller.signal)
  }).then(value => finish(resolve, value), error => finish(reject, error))
})

export function loadHomeData(userId, { onUpdate, signal, requestScope } = {}) {
  // An invalidated Dashboard revision must never join an older in-flight job
  // and label that job's snapshot as fresh data for the new revision.
  const pendingJobs = pendingFor(requestScope)
  let job = pendingJobs.get(userId)
  if (!job) {
    job = { listeners: new Set(), cleanups: new Set(), snapshot: null, controller: new AbortController(), finished: false }
    const publish = snapshot => {
      if (job.controller.signal.aborted) return
      job.snapshot = snapshot
      for (const listener of job.listeners) listener(snapshot)
    }
    job.promise = fetchHomeData(userId, publish, job.controller.signal).finally(() => {
      job.finished = true
      job.controller.abort()
      if (pendingJobs.get(userId) === job) pendingJobs.delete(userId)
      for (const cleanup of job.cleanups) cleanup()
      job.listeners.clear()
    })
    pendingJobs.set(userId, job)
  }
  if (onUpdate && !signal?.aborted) {
    const listener = snapshot => { if (!signal?.aborted) onUpdate(snapshot) }
    const unsubscribe = () => {
      job.listeners.delete(listener)
      signal?.removeEventListener('abort', unsubscribe)
      job.cleanups.delete(unsubscribe)
      if (!job.finished && job.listeners.size === 0) {
        job.controller.abort()
        if (pendingJobs.get(userId) === job) pendingJobs.delete(userId)
      }
    }
    job.listeners.add(listener)
    job.cleanups.add(unsubscribe)
    signal?.addEventListener('abort', unsubscribe, { once: true })
    if (job.snapshot) listener(job.snapshot)
  }
  return job.promise
}

export async function fetchNeedAttachmentCounts(client, companyIds, { signal } = {}) {
  const counts = {}
  const ids = [...new Set(companyIds)]
  // Bound both URL size and response size; Supabase may cap each response.
  for (let start = 0; start < ids.length; start += 100) {
    const group = ids.slice(start, start + 100)
    let afterId = null
    for (;;) {
      if (signal?.aborted) throw abortError()
      let query = client.from('need_attachments').select('company_id, id')
        .in('company_id', group).eq('status', 'active').eq('moderation_status', 'approved')
        .order('id', { ascending: true }).limit(500)
      if (afterId !== null) query = query.gt('id', afterId)
      const { data, error } = await queryWithSignal(query, signal)
      if (error) throw error
      const rows = data || []
      for (const row of rows) counts[row.company_id] = (counts[row.company_id] || 0) + 1
      if (rows.length < 500) break
      const nextId = rows[rows.length - 1]?.id
      if (!nextId || nextId === afterId) throw new Error('Need attachment pagination stalled')
      afterId = nextId
    }
  }
  return counts
}

const checked = result => {
  if (result.error) throw result.error
  return result
}

async function fetchBadgePlans(companies, signal) {
  const badges = []
  for (let offset = 0; offset < companies.length; offset += 500) {
    if (signal.aborted) throw abortError()
    const result = checked(await queryWithSignal(supabase.rpc('hubbing_company_badges', {
      p_company_ids: companies.slice(offset, offset + 500).map(company => company.id),
    }), signal))
    badges.push(...(result.data || []))
  }
  const byId = new Map(badges.map(badge => [badge.company_id, badge]))
  return new Map(companies.map(company => {
    const badge = byId.get(company.id)
    return [company.id, badge?.plan ? [{ plan: badge.plan, status: badge.status || 'active' }] : []]
  }))
}

async function fetchHomeData(userId, publish, signal) {
  // Directory pagination is independent of the current company's lookup.
  // Handle rejection immediately, even if the company lookup is still pending.
  const directoryResult = withinDeadline(sectionSignal => fetchCompanyDirectory(supabase, { signal: sectionSignal }), HOME_SECTION_TIMEOUT_MS, signal)
    .then(data => ({ data }), error => ({ error }))
  const { data: company } = checked(await withinDeadline(sectionSignal => queryWithSignal(supabase.from('companies')
    .select('*').eq('user_id', userId).maybeSingle(), sectionSignal), HOME_COMPANY_TIMEOUT_MS, signal))
  if (!company) return null

  let directory = [], followingRows = [], followerRows = [], subscriptions = new Map()
  const extraCompanies = new Map()
  const stats = { matches: null, followers: null, messages: null, totalCompanies: null, collaborations: null, profileViews: null }
  const sections = Object.fromEntries(['directory', 'following', 'followers', 'messages', 'totalCompanies', 'collaborations', 'profileViews', 'badges', 'attachments'].map(key => [key, 'loading']))
  let attachmentCounts = {}
  const snapshot = () => {
    const companies = new Map([...extraCompanies.values(), ...directory, company].map(row => [row.id, row]))
    const enriched = id => {
      const row = companies.get(id)
      return row && (subscriptions.has(id) ? { ...row, subscriptions: subscriptions.get(id) } : row)
    }
    return {
      company: enriched(company.id),
      opportunityCompanies: directory.filter(row => row.id !== company.id).map(row => enriched(row.id)),
      followingMatches: followingRows.map(row => ({ ...row, company_b: enriched(row.company_b) })).filter(row => row.company_b),
      followerMatches: followerRows.map(row => ({ ...row, company_a: enriched(row.company_a) })).filter(row => row.company_a),
      attachmentCounts, badgeCompanyIds: [...subscriptions.keys()],
      stats: { ...stats }, sections: { ...sections },
    }
  }
  const emit = () => { const data = snapshot(); publish(data); return data }
  emit() // The header and navigation never wait for optional sections.

  const section = async (key, work, apply) => {
    try {
      const value = await withinDeadline(work, HOME_SECTION_TIMEOUT_MS, signal)
      if (signal.aborted) return
      apply(value)
      sections[key] = 'ready'
    } catch (error) {
      if (signal.aborted) return
      sections[key] = 'error'
      // Keep unknown counts unknown: a failed request is never a loaded zero.
      console.warn(`Home ${key} unavailable:`, error?.message || 'request failed')
    }
    emit()
  }
  const relations = async (direction, sectionSignal) => {
    const incoming = direction === 'followers'
    const otherKey = incoming ? 'company_a' : 'company_b'
    const profileKey = incoming ? 'company_a_profile' : 'company_b_profile'
    const { data } = checked(await queryWithSignal(supabase.from('matches')
      .select(`id, company_a, company_b, status, created_at, ${profileKey}:${otherKey}(*)`)
      .eq(incoming ? 'company_b' : 'company_a', company.id).eq('status', 'pending'), sectionSignal))
    if (sectionSignal.aborted) throw abortError()
    const rows = (data || []).filter(row => row[otherKey] && row[otherKey] !== company.id)
    const missingIds = []
    for (const row of rows) {
      if (row[profileKey]) extraCompanies.set(row[otherKey], row[profileKey])
      else if (!directory.some(candidate => candidate.id === row[otherKey])) missingIds.push(row[otherKey])
    }
    if (missingIds.length) {
      const result = checked(await queryWithSignal(supabase.from('companies').select('*').in('id', [...new Set(missingIds)]), sectionSignal))
      for (const row of result.data || []) extraCompanies.set(row.id, row)
    }
    return rows
  }
  const directoryTask = section('directory', async () => {
    const result = await directoryResult
    if (result.error) throw result.error
    return result.data
  }, rows => { directory = rows })
  const badgeTask = directoryTask.then(() => {
    if (signal.aborted) return
    if (sections.directory !== 'ready') { sections.badges = 'error'; emit(); return }
    return section('badges', sectionSignal => fetchBadgePlans([...new Map([company, ...directory].map(row => [row.id, row])).values()], sectionSignal), value => { subscriptions = value })
  })
  const attachmentTask = directoryTask.then(() => {
    if (signal.aborted) return
    if (sections.directory !== 'ready') { sections.attachments = 'error'; emit(); return }
    return section('attachments', sectionSignal => fetchNeedAttachmentCounts(supabase, directory.filter(row => row.id !== company.id).map(row => row.id), { signal: sectionSignal }), value => { attachmentCounts = value })
  })
  await Promise.all([
    directoryTask, badgeTask, attachmentTask,
    section('following', sectionSignal => relations('following', sectionSignal), rows => { followingRows = rows; stats.matches = rows.length }),
    section('followers', sectionSignal => relations('followers', sectionSignal), rows => { followerRows = rows; stats.followers = rows.length }),
    section('messages', async sectionSignal => checked(await queryWithSignal(supabase.from('messages').select('*', { count: 'exact', head: true }).eq('sender_id', company.id), sectionSignal)), result => { stats.messages = result.count ?? null }),
    section('totalCompanies', async sectionSignal => checked(await queryWithSignal(supabase.from('companies').select('*', { count: 'exact', head: true }), sectionSignal)), result => { stats.totalCompanies = result.count ?? null }),
    section('collaborations', async sectionSignal => {
      const { data } = checked(await queryWithSignal(supabase.from('need_completions')
        .select('id,client_company_id,provider_company_id,status,show_on_provider_profile')
        .or(`client_company_id.eq.${company.id},provider_company_id.eq.${company.id}`), sectionSignal))
      return countSuccessfulCollaborationsForCompany(company.id, data || [], { includeHiddenProvider: true })
    }, value => { stats.collaborations = value }),
    section('profileViews', async sectionSignal => {
      const result = checked(await queryWithSignal(supabase.rpc('hubbing_profile_visit_count'), sectionSignal))
      if (result.data == null || !Number.isFinite(Number(result.data))) throw new Error('Unavailable visit counter')
      return Number(result.data)
    }, value => { stats.profileViews = value }),
  ])
  return snapshot()
}
